import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Alert, IconButton, Tooltip, CircularProgress, Chip, useTheme
} from '@mui/material';
import { Close, Restore, Save, WarningAmber } from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../../services/axiosInstance';
import { useLanguage } from '../../contexts/LanguageContext';

export interface ManualCostLineRow {
  detailId: number;
  lineLabel: string;        // e.g. "1" or item code
  itemCode: string;
  itemName: string;
  unitName: string;
  quantity: number;
  originalCost: number;     // FIFO/auto cost (from server)
  currentUnitCost: number;  // What's stored now (either auto or previous manual)
  isManualCost: boolean;
  manualCostByName?: string | null;
  manualCostAt?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  docType: 'stockIssue' | 'stockCount';
  docId: number;
  docNumber: string;
  docStatus: string;            // Draft / Pending / Completed / etc — NOT Posted/Approved
  lines: ManualCostLineRow[];
}

const ManualCostEditDialog: React.FC<Props> = ({ open, onClose, onSaved, docType, docId, docNumber, docStatus, lines }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Theme-aware palette
  const c = {
    headerBg: isDark ? '#2D1B05' : '#FFF3E0',
    headerText: isDark ? '#FFD180' : '#5D4037',
    manualRowBg: isDark ? 'rgba(255, 152, 0, 0.18)' : 'rgba(255, 152, 0, 0.08)',
    rowHover: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    fifoBlue: isDark ? '#64B5F6' : '#1565C0',
    diffPos: isDark ? '#81C784' : '#2E7D32',
    diffNeg: isDark ? '#E57373' : '#C62828',
    diffZero: theme.palette.text.secondary,
    manualChipBg: isDark ? '#F57C00' : '#FF9800',
    autoChipBg: isDark ? 'rgba(255,255,255,0.15)' : '#E0E0E0',
    autoChipText: isDark ? '#E0E0E0' : 'rgba(0,0,0,0.87)',
    secondaryText: theme.palette.text.secondary,
    primaryText: theme.palette.text.primary,
    inputBg: isDark ? 'rgba(255,255,255,0.05)' : 'white',
  };

  const [edits, setEdits] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const initial: Record<number, string> = {};
      for (const l of lines) initial[l.detailId] = l.currentUnitCost.toString();
      setEdits(initial);
    }
  }, [open, lines]);

  const isBlocked = docStatus === 'Posted' || docStatus === 'Approved';
  const fmt = (n: number) => (n || 0).toFixed(7).replace(/0+$/, '').replace(/\.$/, '');

  const handleResetLine = (detailId: number, original: number) => {
    setEdits(prev => ({ ...prev, [detailId]: original.toString() }));
  };

  const buildChangedLines = () => {
    const changed: { detailId: number; manualUnitCost: number }[] = [];
    for (const l of lines) {
      const raw = edits[l.detailId];
      if (raw === undefined || raw === '') continue;
      const v = parseFloat(raw);
      if (isNaN(v) || v < 0) continue;
      if (Math.abs(v - l.currentUnitCost) > 0.0000001) {
        changed.push({ detailId: l.detailId, manualUnitCost: v });
      }
    }
    return changed;
  };

  const handleSave = async () => {
    const changed = buildChangedLines();
    if (changed.length === 0) {
      toast.info(isRTL ? 'لا توجد تغييرات' : 'No changes');
      return;
    }
    setSaving(true);
    try {
      const url = docType === 'stockIssue'
        ? `/StockIssues/${docId}/manual-costs`
        : `/StockCounts/${docId}/manual-costs`;
      const body = docType === 'stockIssue'
        ? { lines: changed.map(c => ({ issueDetailId: c.detailId, manualUnitCost: c.manualUnitCost })) }
        : { lines: changed.map(c => ({ countDetailId: c.detailId, manualUnitCost: c.manualUnitCost })) };
      await axiosInstance.post(url, body);
      toast.success(isRTL ? `تم حفظ ${changed.length} تعديل` : `Saved ${changed.length} changes`);
      onSaved();
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message || (isRTL ? 'فشل الحفظ' : 'Save failed');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleResetManual = async () => {
    const manualIds = lines.filter(l => l.isManualCost).map(l => l.detailId);
    if (manualIds.length === 0) {
      toast.info(isRTL ? 'لا توجد سطور يدوية للإلغاء' : 'No manual rows to reset');
      return;
    }
    setSaving(true);
    try {
      const url = docType === 'stockIssue'
        ? `/StockIssues/${docId}/reset-manual-costs`
        : `/StockCounts/${docId}/reset-manual-costs`;
      const body = docType === 'stockIssue'
        ? { issueDetailIds: manualIds }
        : { countDetailIds: manualIds };
      await axiosInstance.post(url, body);
      toast.success(isRTL ? `تم إلغاء التعديل اليدوي لـ ${manualIds.length} سطر` : `Reset ${manualIds.length} manual rows`);
      onSaved();
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message || (isRTL ? 'فشل' : 'Failed');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <DialogTitle sx={{ background: 'linear-gradient(90deg, #FF6F00 0%, #FFA000 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {isRTL ? '🔧 تعديل التكلفة يدوياً' : '🔧 Manual Cost Override'}
          </Typography>
          <Typography variant="caption">{docNumber} · {docStatus}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {isBlocked && (
          <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
            {isRTL
              ? `لا يمكن تعديل التكلفة لسند بحالة "${docStatus}". ألغِ الترحيل أو الاعتماد أولاً ثم عدّل التكلفة.`
              : `Cannot edit cost for status "${docStatus}". Unpost or unapprove first, then edit.`}
          </Alert>
        )}
        <Alert severity="error" sx={{ mb: 2 }}>
          {isRTL
            ? '⚠️ صلاحية حساسة جداً للضرورة القصوى فقط. أي فرق بين التكلفة اليدوية وتكلفة FIFO الواقعية يُسجَّل تلقائياً كقيد تسوية على حساب "تسوية تكلفة المخزون" — يجب ربطه في إعدادات المحاسبة قبل الترحيل.'
            : '⚠️ Highly sensitive permission — emergency use only. Any difference between manual cost and actual FIFO cost will be auto-recorded as a balancing journal entry on the "Inventory Cost Adjustment" account, which must be linked in Accounting Settings before posting.'}
        </Alert>

        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: c.headerBg, '& th': { color: c.headerText, fontWeight: 700, borderBottom: 2, borderColor: 'divider' } }}>
                <TableCell>#</TableCell>
                <TableCell>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                <TableCell>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                <TableCell align="right">{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                <TableCell align="right">{isRTL ? 'التكلفة الأصلية (FIFO)' : 'Original (FIFO)'}</TableCell>
                <TableCell align="right">{isRTL ? 'التكلفة اليدوية' : 'Manual Cost'}</TableCell>
                <TableCell align="right">{isRTL ? 'الفارق/الوحدة' : 'Diff/Unit'}</TableCell>
                <TableCell align="right">{isRTL ? 'الفارق الإجمالي' : 'Total Diff'}</TableCell>
                <TableCell>{isRTL ? 'الحالة' : 'State'}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => {
                const cur = parseFloat(edits[l.detailId] || '0') || 0;
                const diff = cur - l.originalCost;
                const totalDiff = diff * l.quantity;
                return (
                  <TableRow
                    key={l.detailId}
                    sx={{
                      bgcolor: l.isManualCost ? c.manualRowBg : 'inherit',
                      '&:hover': { bgcolor: l.isManualCost ? c.manualRowBg : c.rowHover },
                      '& td': { color: c.primaryText }
                    }}
                  >
                    <TableCell>{l.lineLabel}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} sx={{ color: c.primaryText }}>{l.itemCode}</Typography>
                      <Typography variant="caption" sx={{ color: c.secondaryText }}>{l.itemName}</Typography>
                    </TableCell>
                    <TableCell>{l.unitName}</TableCell>
                    <TableCell align="right">{l.quantity.toFixed(3)}</TableCell>
                    <TableCell align="right" sx={{ color: c.fifoBlue, fontWeight: 600 }}>{fmt(l.originalCost)}</TableCell>
                    <TableCell align="right" sx={{ width: 150 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={edits[l.detailId] ?? ''}
                        onChange={e => setEdits(prev => ({ ...prev, [l.detailId]: e.target.value }))}
                        inputProps={{ step: '0.0000001', min: 0, style: { textAlign: 'right' } }}
                        disabled={isBlocked || saving}
                        sx={{
                          width: 140,
                          '& .MuiOutlinedInput-root': { bgcolor: c.inputBg }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ color: diff === 0 ? c.diffZero : (diff > 0 ? c.diffPos : c.diffNeg), fontWeight: 600 }}>
                      {diff === 0 ? '-' : (diff > 0 ? '+' : '') + fmt(diff)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: totalDiff === 0 ? c.diffZero : (totalDiff > 0 ? c.diffPos : c.diffNeg), fontWeight: 600 }}>
                      {totalDiff === 0 ? '-' : (totalDiff > 0 ? '+' : '') + totalDiff.toFixed(7).replace(/0+$/, '').replace(/\.$/, '')}
                    </TableCell>
                    <TableCell>
                      {l.isManualCost ? (
                        <Chip size="small" label={isRTL ? '🔧 يدوي' : '🔧 Manual'} sx={{ bgcolor: c.manualChipBg, color: 'white', fontWeight: 600 }} />
                      ) : (
                        <Chip size="small" label={isRTL ? 'تلقائي' : 'Auto'} sx={{ bgcolor: c.autoChipBg, color: c.autoChipText }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={isRTL ? 'استرجاع للقيمة الأصلية' : 'Reset to original'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleResetLine(l.detailId, l.originalCost)}
                            disabled={isBlocked || saving}
                            sx={{ color: c.secondaryText }}
                          >
                            <Restore fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          color="warning"
          variant="outlined"
          onClick={handleResetManual}
          disabled={isBlocked || saving || lines.every(l => !l.isManualCost)}
          startIcon={<Restore />}
        >
          {isRTL ? 'إلغاء كل التعديلات اليدوية' : 'Reset all manual rows'}
        </Button>
        <Box>
          <Button onClick={onClose} disabled={saving}>
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isBlocked || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ ml: 1 }}
          >
            {isRTL ? 'حفظ التعديل' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ManualCostEditDialog;
